// src/pages/order_management/OrderPurchasingPanel.tsx
//
// Purchasing on a Sales Order (EE-253 P3, spec §8): every requisition line
// raised for the order - what was asked for, which Inquiry buys it and how
// far that has got, from which supplier, how much is ready to use, and the
// line's share of the Inquiry's price and transport. Read-only and derived
// on read; an Inquiry can buy for several orders, so a share is this order's
// part of it.
import { FunctionComponent, useContext } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import StatusToken from "components/Dashboard/StatusToken";
import { useOrderPurchasing } from "hooks/useRequisitionPool";
import { InquiryStatusEnum } from "types/InquiryTypes";
import { INQUIRY_STATUS_COLORS } from "pages/outsourcing_management/constants/inquiryStatusColors";
import AppLink from "pages/outsourcing_management/AppLink";
import { reformatDate } from "util/DateUtil";
import { formatCents, formatCentsOrDash } from "util/money";

const HEADERS = [
  "Requisition / line",
  "Type",
  "What",
  "Required",
  "Required by",
  "Inquiry",
  "Supplier",
  "Ready to use",
  "Cost share",
  "Transport share",
];

const inquiryNo = (id: number) => `INQ-${String(id).padStart(6, "0")}`;

const OrderPurchasingPanel: FunctionComponent<{ orderId: number }> = ({ orderId }) => {
  const { token } = useContext(AppStateContext);
  const { data, isLoading, error } = useOrderPurchasing(token, orderId);

  if (isLoading)
    return (
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <CircularProgress size={14} />
        <Typography variant="body2" color="text.secondary">
          Loading purchasing…
        </Typography>
      </Box>
    );
  if (error)
    return <Alert severity="error">Could not load purchasing: {String((error as any)?.message ?? error)}</Alert>;
  const rows = (data?.requisitions ?? []).flatMap((req) =>
    req.lines.map((line) => ({ req, line }))
  );
  if (!data || !rows.length)
    return (
      <Typography variant="body2" color="text.secondary" data-testid="order-purchasing-empty">
        No requisitions for this order.
      </Typography>
    );

  return (
    <Box data-testid="order-purchasing">
      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {HEADERS.map((h) => (
                <TableCell key={h} sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ req, line }) => (
              <TableRow
                key={line.requisitionItemId}
                data-testid={`order-purchasing-line-${line.requisitionItemId}`}
                // A line cancelled on its own stays listed, greyed: on a sent
                // Inquiry its goods may still come, at no share of the cost.
                sx={line.lineState === "CANCELLED" ? { "& > .MuiTableCell-root": { color: "text.disabled" } } : undefined}
              >
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <AppLink to={`/requisitions/${req.id}`}>{req.requisitionNo}</AppLink> / L
                  {line.lineNo ?? "?"}
                </TableCell>
                <TableCell>{line.typeLabel ?? "-"}</TableCell>
                <TableCell>
                  {line.what}
                  {line.part && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      for {line.part}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{line.required || "-"}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {line.requiredBy ? reformatDate(line.requiredBy) : "-"}
                </TableCell>
                <TableCell>
                  {line.inquiryId ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
                      <AppLink to={`/inquiries/${line.inquiryId}`}>{inquiryNo(line.inquiryId)}</AppLink>
                      <StatusToken
                        label={line.lineStateLabel ?? ""}
                        colors={INQUIRY_STATUS_COLORS[(line.inquiryState ?? "DRAFT") as InquiryStatusEnum]}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {line.lineStateLabel ?? "-"}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{line.supplier ?? "-"}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{line.ready ?? "-"}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{formatCentsOrDash(line.costShareCents)}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {formatCentsOrDash(line.transportShareCents)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow data-testid="order-purchasing-totals">
              <TableCell colSpan={8} sx={{ fontWeight: "bold" }}>
                Total for this order
                {!data.totals.complete && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Not every Inquiry is priced yet - the totals are what is known so far.
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                {formatCents(data.totals.goodsCents)}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                {formatCents(data.totals.transportCents)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default OrderPurchasingPanel;
